import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, IconButton, Alert, Chip, Paper,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  FormControl, InputLabel, Select, MenuItem, FormGroup, FormControlLabel,
  Checkbox, Tooltip, Snackbar, CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  ContentCopy as ContentCopyIcon,
  VpnKey as VpnKeyIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Description as DescriptionIcon,
  Extension as ExtensionIcon,
} from '@mui/icons-material';
import { Link } from '@tanstack/react-router';
import { MainLayout } from '../components/MainLayout';
import { EmptyState } from '../components/EmptyState';
import {
  PATService,
  type PATResponse,
  type CreatePATResponse,
  type CreatePATRequest,
} from '../services/pat-service';

/** Available scopes that can be granted to a PAT. */
const AVAILABLE_SCOPES = ['read', 'write'];

/** Expiry presets for new tokens. */
const EXPIRY_OPTIONS = [
  { label: '30 days', value: 30 },
  { label: '90 days', value: 90 },
  { label: '1 year', value: 365 },
  { label: 'Never', value: 0 },
];

/** Default form state for creating a new token. */
const DEFAULT_FORM: CreatePATRequest = {
  name: '',
  scopes: ['read'],
  expires_in: 90,
};

/**
 * Formats an ISO date string to a user-friendly locale string.
 *
 * Args:
 *     dateStr (string | undefined): ISO date string.
 *
 * Returns:
 *     string: Formatted date or dash placeholder.
 */
function formatDate(dateStr?: string): string {
  if (!dateStr) return '--';
  return new Date(dateStr).toLocaleDateString('en-CA', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/**
 * Checks whether a token has expired based on its expires_at field.
 *
 * Args:
 *     token (PATResponse): The token to check.
 *
 * Returns:
 *     boolean: True if the token has expired.
 */
function isExpired(token: PATResponse): boolean {
  if (!token.expires_at) return false;
  return new Date(token.expires_at) < new Date();
}

export const APITokensPage: React.FC = () => {
  const [tokens, setTokens] = useState<PATResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<string | null>(null);

  // Create dialog state
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [form, setForm] = useState<CreatePATRequest>({ ...DEFAULT_FORM });
  const [saving, setSaving] = useState(false);

  // Newly created token display state
  const [newlyCreatedToken, setNewlyCreatedToken] = useState<CreatePATResponse | null>(null);
  const [tokenVisible, setTokenVisible] = useState(false);

  // Revoke confirmation dialog state
  const [revokeTarget, setRevokeTarget] = useState<PATResponse | null>(null);
  const [revoking, setRevoking] = useState(false);

  /** Fetch all tokens from the backend. */
  const fetchTokens = useCallback(async () => {
    try {
      setLoading(true);
      const result = await PATService.list();
      setTokens(result.tokens);
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Failed to load API tokens');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTokens();
  }, [fetchTokens]);

  /** Open the create dialog and reset the form. */
  const openCreateDialog = () => {
    setForm({ ...DEFAULT_FORM });
    setCreateDialogOpen(true);
  };

  /** Close the create dialog. */
  const closeCreateDialog = () => {
    setCreateDialogOpen(false);
  };

  /** Toggle a scope checkbox on or off. */
  const toggleScope = (scope: string) => {
    setForm((prev) => {
      const current = prev.scopes || [];
      const next = current.includes(scope)
        ? current.filter((s) => s !== scope)
        : [...current, scope];
      return { ...prev, scopes: next };
    });
  };

  /** Submit the create token form. */
  const handleCreate = async () => {
    try {
      setSaving(true);
      const payload: CreatePATRequest = {
        name: form.name,
        scopes: form.scopes,
      };
      // Reason: Only include expires_in when it is a positive number (0 means "never").
      if (form.expires_in && form.expires_in > 0) {
        payload.expires_in = form.expires_in;
      }
      const created = await PATService.create(payload);
      setNewlyCreatedToken(created);
      setTokenVisible(true);
      closeCreateDialog();
      setSnackbar('API token created successfully');
      fetchTokens();
    } catch (err: any) {
      setError(err.message || 'Failed to create API token');
    } finally {
      setSaving(false);
    }
  };

  /** Copy the raw token value to the clipboard. */
  const copyTokenToClipboard = async (token: string) => {
    try {
      await navigator.clipboard.writeText(token);
      setSnackbar('Token copied to clipboard');
    } catch {
      setSnackbar('Failed to copy token');
    }
  };

  /** Open the revoke confirmation dialog. */
  const openRevokeDialog = (token: PATResponse) => {
    setRevokeTarget(token);
  };

  /** Close the revoke confirmation dialog. */
  const closeRevokeDialog = () => {
    setRevokeTarget(null);
  };

  /** Revoke the target token. */
  const handleRevoke = async () => {
    if (!revokeTarget) return;
    try {
      setRevoking(true);
      await PATService.revoke(revokeTarget.id);
      setSnackbar(`Token "${revokeTarget.name}" revoked`);
      closeRevokeDialog();
      fetchTokens();
    } catch (err: any) {
      setError(err.message || 'Failed to revoke token');
    } finally {
      setRevoking(false);
    }
  };

  /** Dismiss the newly-created token banner. */
  const dismissNewToken = () => {
    setNewlyCreatedToken(null);
    setTokenVisible(false);
  };

  const isFormValid = form.name.trim().length > 0;

  if (loading) {
    return (
      <MainLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <Box>
        {/* Header */}
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Typography variant="h4" fontWeight="bold">API Tokens</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Create and manage Personal Access Tokens to authenticate API requests
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              component={Link}
              to="/extensions"
              variant="outlined"
              startIcon={<ExtensionIcon />}
            >
              MCP Setup
            </Button>
            <Button
              component={Link}
              to="/api-docs"
              variant="outlined"
              startIcon={<DescriptionIcon />}
            >
              API Docs
            </Button>
            <Button variant="contained" startIcon={<AddIcon />} onClick={openCreateDialog}>
              New Token
            </Button>
          </Box>
        </Box>

        {/* Error alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Newly created token banner */}
        {newlyCreatedToken && (
          <Alert
            severity="warning"
            sx={{ mb: 3 }}
            onClose={dismissNewToken}
            icon={<VpnKeyIcon />}
          >
            <Typography variant="subtitle2" gutterBottom>
              Your new API token has been created. Copy it now -- it will not be shown again.
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
              <Paper
                variant="outlined"
                sx={{
                  px: 2,
                  py: 1,
                  fontFamily: 'monospace',
                  fontSize: '0.875rem',
                  bgcolor: 'background.default',
                  flexGrow: 1,
                  wordBreak: 'break-all',
                }}
              >
                {tokenVisible ? newlyCreatedToken.token : '************************************'}
              </Paper>
              <Tooltip title={tokenVisible ? 'Hide token' : 'Show token'}>
                <IconButton size="small" onClick={() => setTokenVisible(!tokenVisible)}>
                  {tokenVisible ? <VisibilityOffIcon /> : <VisibilityIcon />}
                </IconButton>
              </Tooltip>
              <Tooltip title="Copy to clipboard">
                <IconButton size="small" onClick={() => copyTokenToClipboard(newlyCreatedToken.token)}>
                  <ContentCopyIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Alert>
        )}

        {/* Info alert */}
        <Alert severity="info" sx={{ mb: 3 }}>
          Personal Access Tokens (PATs) allow you to authenticate API requests programmatically.
          Include the token in the <code>Authorization</code> header:{' '}
          <code>Bearer fw_your_token_here</code>
        </Alert>

        {/* Tokens table or empty state */}
        {tokens.length === 0 ? (
          <EmptyState
            icon={<VpnKeyIcon />}
            title="No API Tokens"
            description="Create a Personal Access Token to start using the FolioWorth API."
            actionLabel="Create First Token"
            onAction={openCreateDialog}
          />
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Token Prefix</TableCell>
                  <TableCell>Scopes</TableCell>
                  <TableCell>Last Used</TableCell>
                  <TableCell>Expires</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tokens.map((token) => {
                  const expired = isExpired(token);
                  return (
                    <TableRow key={token.id} sx={{ opacity: token.is_active && !expired ? 1 : 0.6 }}>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {token.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Created {formatDate(token.created_at)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontFamily="monospace">
                          {token.token_prefix}...
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                          {token.scopes.map((scope) => (
                            <Chip key={scope} label={scope} size="small" variant="outlined" />
                          ))}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDate(token.last_used_at)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color={expired ? 'error.main' : 'text.primary'}>
                          {token.expires_at ? formatDate(token.expires_at) : 'Never'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {!token.is_active ? (
                          <Chip label="Revoked" size="small" color="error" variant="outlined" />
                        ) : expired ? (
                          <Chip label="Expired" size="small" color="warning" variant="outlined" />
                        ) : (
                          <Chip label="Active" size="small" color="success" variant="outlined" />
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {token.is_active && (
                          <Tooltip title="Revoke token">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => openRevokeDialog(token)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Create Token Dialog */}
        <Dialog open={createDialogOpen} onClose={closeCreateDialog} maxWidth="sm" fullWidth>
          <DialogTitle>Create API Token</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 1 }}>
              <TextField
                label="Token Name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                fullWidth
                autoFocus
                placeholder="e.g. CI/CD Pipeline, Portfolio Tracker"
                helperText="A descriptive name to help you identify this token"
              />
              <FormControl component="fieldset">
                <Typography variant="subtitle2" gutterBottom>
                  Scopes
                </Typography>
                <FormGroup row>
                  {AVAILABLE_SCOPES.map((scope) => (
                    <FormControlLabel
                      key={scope}
                      control={
                        <Checkbox
                          checked={form.scopes?.includes(scope) || false}
                          onChange={() => toggleScope(scope)}
                        />
                      }
                      label={scope.charAt(0).toUpperCase() + scope.slice(1)}
                    />
                  ))}
                </FormGroup>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>Expiration</InputLabel>
                <Select
                  value={form.expires_in ?? 90}
                  label="Expiration"
                  onChange={(e) =>
                    setForm({ ...form, expires_in: e.target.value as number })
                  }
                >
                  {EXPIRY_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeCreateDialog}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleCreate}
              disabled={!isFormValid || saving}
              startIcon={saving ? <CircularProgress size={18} /> : <AddIcon />}
            >
              Create Token
            </Button>
          </DialogActions>
        </Dialog>

        {/* Revoke Confirmation Dialog */}
        <Dialog open={!!revokeTarget} onClose={closeRevokeDialog} maxWidth="xs" fullWidth>
          <DialogTitle>Revoke Token</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to revoke the token{' '}
              <strong>{revokeTarget?.name}</strong>? This action cannot be undone.
              Any applications using this token will lose access immediately.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeRevokeDialog}>Cancel</Button>
            <Button
              variant="contained"
              color="error"
              onClick={handleRevoke}
              disabled={revoking}
              startIcon={revoking ? <CircularProgress size={18} /> : <DeleteIcon />}
            >
              Revoke
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar for success messages */}
        <Snackbar
          open={!!snackbar}
          autoHideDuration={4000}
          onClose={() => setSnackbar(null)}
          message={snackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        />
      </Box>
    </MainLayout>
  );
};
